import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { X, Sparkles, ArrowRight, Gift, Zap, Shield, CheckCircle, AlertCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useContent } from '../context/ContentContext';
import axios from 'axios';
import { toast } from 'sonner';
import { trackClick } from '../services/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

const WaitlistDialog = ({ onClose }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [duplicateError, setDuplicateError] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const { isRTL, language } = useLanguage();
  const { getContent } = useContent();
  
  // Get dynamic content based on language
  const section = language === 'ar' ? 'waitlistDialog_ar' : 'waitlistDialog';

  useEffect(() => {
    // Trigger entrance animation
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    // Track click
    trackClick('waitlist-dialog-submit', 'Get Early Access', 'Landing Page');
    
    setLoading(true);
    setDuplicateError(false);
    
    try {
      await axios.post(`${API}/api/waitlist`, {
        name: email.split('@')[0],
        email,
        interest_type: 'user',
        utm_source: 'dialog'
      });
      setSubmitted(true);
    } catch (error) {
      if (error.response?.status === 400) {
        // Email already registered
        setDuplicateError(true);
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    }
    setLoading(false);
  };

  const benefits = [
    { icon: Gift, text: getContent(section, 'benefit1', 'Free for 6 months for first 100 users') },
    { icon: Zap, text: getContent(section, 'benefit2', 'Priority access to new features') },
    { icon: Shield, text: getContent(section, 'benefit3', 'Direct founder support') }
  ];

  // Success message content
  const successContent = {
    en: {
      title: 'Welcome to Qykfin!',
      message: 'Thank you for joining the waitlist, we\'ll be in touch soon.',
      skip: 'Got it, continue to site'
    },
    ar: {
      title: 'مرحباً بك في كيكفن!',
      message: 'شكراً لانضمامك إلى قائمة الانتظار، سنتواصل معك قريباً.',
      skip: 'حسناً، متابعة للموقع'
    }
  };

  // Duplicate error content
  const duplicateContent = {
    en: {
      title: 'Email already registered',
      message: 'This email is already on our waitlist. We\'ll be in touch soon!',
      skip: 'Got it, continue to site'
    },
    ar: {
      title: 'البريد الإلكتروني مسجل مسبقاً',
      message: 'هذا البريد الإلكتروني موجود بالفعل في قائمة الانتظار. سنتواصل معك قريباً!',
      skip: 'حسناً، متابعة للموقع'
    }
  };

  return (
    <div 
      className={`fixed inset-0 z-[100] flex items-center justify-center p-4 transition-all duration-300 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      {/* Backdrop */}
      <div 
        className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleClose}
      />
      
      {/* Dialog */}
      <div 
        className={`relative w-full max-w-md transform transition-all duration-500 ease-out ${
          isVisible 
            ? 'opacity-100 translate-y-0 scale-100' 
            : 'opacity-0 translate-y-8 scale-95'
        }`}
      >
        {/* Glow Effect */}
        <div className="absolute -inset-1 bg-gradient-to-r from-[#0071E3] via-[#5856D6] to-[#0071E3] rounded-3xl blur-xl opacity-30 animate-pulse"></div>
        
        {/* Card */}
        <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Close Button */}
          <button 
            onClick={handleClose}
            className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-[#F5F5F7] hover:bg-[#E5E5E5] transition-colors"
            data-testid="dialog-close-btn"
          >
            <X className="w-4 h-4 text-[#6e6e73]" />
          </button>

          {/* Show Success State */}
          {submitted ? (
            <div className="px-8 py-12 text-center">
              <div className="w-20 h-20 bg-[#34C759]/10 rounded-full flex items-center justify-center mx-auto mb-6 animate-scale-in">
                <CheckCircle className="w-10 h-10 text-[#34C759]" />
              </div>
              <h2 className={`text-2xl font-dm font-bold text-[#1D1D1F] mb-3 ${isRTL ? 'text-right' : ''}`}>
                {successContent[language]?.title || successContent.en.title}
              </h2>
              <p className={`text-[#6e6e73] font-inter text-[15px] mb-8 leading-relaxed ${isRTL ? 'text-right' : ''}`}>
                {successContent[language]?.message || successContent.en.message}
              </p>
              <Button 
                onClick={handleClose}
                className="w-full h-12 bg-[#1D1D1F] text-white hover:bg-[#1D1D1F]/90 rounded-xl font-medium text-[15px]"
                data-testid="success-continue-btn"
              >
                {successContent[language]?.skip || successContent.en.skip}
              </Button>
            </div>
          ) : duplicateError ? (
            /* Show Duplicate Error State */
            <div className="px-8 py-12 text-center">
              <div className="w-20 h-20 bg-[#FF9500]/10 rounded-full flex items-center justify-center mx-auto mb-6 animate-scale-in">
                <AlertCircle className="w-10 h-10 text-[#FF9500]" />
              </div>
              <h2 className={`text-2xl font-dm font-bold text-[#1D1D1F] mb-3 ${isRTL ? 'text-right' : ''}`}>
                {duplicateContent[language]?.title || duplicateContent.en.title}
              </h2>
              <p className={`text-[#6e6e73] font-inter text-[15px] mb-8 leading-relaxed ${isRTL ? 'text-right' : ''}`}>
                {duplicateContent[language]?.message || duplicateContent.en.message}
              </p>
              <Button 
                onClick={handleClose}
                className="w-full h-12 bg-[#1D1D1F] text-white hover:bg-[#1D1D1F]/90 rounded-xl font-medium text-[15px]"
                data-testid="duplicate-continue-btn"
              >
                {duplicateContent[language]?.skip || duplicateContent.en.skip}
              </Button>
            </div>
          ) : (
            /* Show Form */
            <>
              {/* Header with Animation */}
              <div className="relative bg-gradient-to-br from-[#0071E3] to-[#5856D6] px-8 pt-10 pb-8 overflow-hidden">
                {/* Animated circles */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 animate-float"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2 animate-float-delayed"></div>
                
                {/* Sparkle Icon */}
                <div className="relative">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-5 animate-bounce-subtle backdrop-blur-sm">
                    <Sparkles className="w-8 h-8 text-white" />
                  </div>
                  
                  <h2 className={`text-2xl md:text-3xl font-dm font-bold text-white mb-2 ${isRTL ? 'text-right' : ''}`}>
                    {getContent(section, 'title', 'Join the Future')}
                  </h2>
                  <p className={`text-white/80 font-inter text-[15px] ${isRTL ? 'text-right' : ''}`}>
                    {getContent(section, 'subtitle', 'Get early access to AI-powered financial intelligence')}
                  </p>
                </div>
              </div>

              {/* Content */}
              <div className="px-8 py-6">
                {/* Benefits */}
                <div className="space-y-3 mb-6">
                  {benefits.map((benefit, index) => (
                    <div 
                      key={index}
                      className={`flex items-center gap-3 opacity-0 animate-fade-in-up ${isRTL ? 'flex-row-reverse' : ''}`}
                      style={{ animationDelay: `${(index + 1) * 100}ms`, animationFillMode: 'forwards' }}
                    >
                      <div className="w-8 h-8 rounded-lg bg-[#34C759]/10 flex items-center justify-center flex-shrink-0">
                        <benefit.icon className="w-4 h-4 text-[#34C759]" />
                      </div>
                      <span className="text-[#1D1D1F] font-inter text-[14px]">{benefit.text}</span>
                    </div>
                  ))}
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-3">
                  <div className="relative group">
                    <Input
                      type="email"
                      placeholder={getContent(section, 'emailPlaceholder', 'Enter your email')}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`h-12 px-5 rounded-xl border-[#E5E5E5] bg-[#F5F5F7] text-[#1D1D1F] placeholder:text-[#86868b] font-inter focus:border-[#0071E3] focus:ring-[#0071E3]/20 focus:bg-white transition-all duration-300 text-[15px] ${isRTL ? 'text-right' : ''}`}
                      data-testid="dialog-email-input"
                      autoFocus
                    />
                    <div className="absolute inset-0 rounded-xl border-2 border-transparent group-focus-within:border-[#0071E3]/20 pointer-events-none transition-all duration-300"></div>
                  </div>
                  
                  <Button 
                    type="submit"
                    disabled={loading}
                    className={`w-full h-12 bg-[#1D1D1F] text-white hover:bg-[#1D1D1F]/90 rounded-xl font-medium text-[15px] flex items-center justify-center gap-2 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] ${isRTL ? 'flex-row-reverse' : ''}`}
                    data-testid="dialog-submit-btn"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    ) : (
                      <>
                        {getContent(section, 'cta', 'Get Early Access')}
                        <ArrowRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
                      </>
                    )}
                  </Button>
                </form>

                {/* Skip Link */}
                <button 
                  onClick={handleClose}
                  className="w-full mt-4 text-center text-[#86868b] text-[13px] font-inter hover:text-[#6e6e73] transition-colors"
                  data-testid="dialog-skip-btn"
                >
                  {getContent(section, 'skip', 'Skip for now, maybe later')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WaitlistDialog;
